import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { OccEndpointsService } from '@spartacus/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { finalize, switchMap, take } from 'rxjs/operators';
import { KnBrQuote } from 'src/app/models/kn-br-quote.model';
import { KN_BR_CUSTOMER_CURRENT, KNBR_REQUEST_QUOTES_LIST } from 'src/constants/api.endpoints.constant';
import { KnBrCustomerContextService } from '../store/kn-br-customer-context/kn-br-customer-context.service';

@Injectable({
  providedIn: 'root',
})
export class KnBrRequestQuoteListingService {
  private readonly defaultSortCode = 'byCreationDate';
  private readonly listSubject = new BehaviorSubject<KnBrQuote[]>([]);
  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  private readonly paginateSubject = new BehaviorSubject<any>(undefined);
  private readonly sortsSubject = new BehaviorSubject<any>([]);
  private readonly searchCriteriaSubject = new BehaviorSubject<any>(undefined);

  readonly list$: Observable<KnBrQuote[]> = this.listSubject.asObservable();
  readonly loading$: Observable<boolean> = this.loadingSubject.asObservable();
  readonly paginate$: Observable<any> = this.paginateSubject.asObservable();
  readonly sorts$: Observable<any> = this.sortsSubject.asObservable();
  readonly searchCriteria$: Observable<any> = this.searchCriteriaSubject.asObservable();

  constructor(
    private endPointService: OccEndpointsService,
    private httpClient: HttpClient,
    private knBrCustomerContextService: KnBrCustomerContextService
  ) {}

  search(searchForm: KnBrQuote | any): void {
    const payload = searchForm || {};
    const currentPage = payload?.currentPage || 0;

    if (currentPage === 0) {
      this.reset();
    }

    this.loadingSubject.next(true);
    this.searchCriteriaSubject.next(payload);

    this.knBrCustomerContextService
      .getActiveCustomerNumber()
      .pipe(
        take(1),
        switchMap((customerNumber) => {
          const customerId = customerNumber || KN_BR_CUSTOMER_CURRENT;
          const url = this.endPointService.buildUrl(KNBR_REQUEST_QUOTES_LIST, {
            urlParams: {
              customerId,
            },
            queryParams: {
              ...payload,
              customerId,
            },
          });

          return this.httpClient.get<any>(url);
        }),
        finalize(() => this.loadingSubject.next(false))
      )
      .subscribe({
        next: (response) => {
          const incomingQuotes = this.extractQuotes(response);
          const mergedQuotes = currentPage > 0 ? [...this.listSubject.value, ...incomingQuotes] : incomingQuotes;
          const activeSortCode = payload?.sort || response?.pagination?.sort || this.defaultSortCode;

          this.listSubject.next(mergedQuotes);
          this.paginateSubject.next(this.extractPagination(response, mergedQuotes.length, payload, activeSortCode));
          this.sortsSubject.next(this.extractSorts(response, activeSortCode));
        },
        error: () => {
          this.sortsSubject.next(this.sortsSubject.value?.length ? this.sortsSubject.value : this.buildDefaultSorts(this.defaultSortCode));
        },
      });
  }

  reset(): void {
    this.listSubject.next([]);
    this.paginateSubject.next(undefined);
  }

  private extractQuotes(response: any): KnBrQuote[] {
    if (!response) {
      return [];
    }

    if (Array.isArray(response?.quotes)) {
      return response.quotes;
    }

    if (Array.isArray(response?.requestQuotes)) {
      return response.requestQuotes;
    }

    if (Array.isArray(response?.results)) {
      return response.results;
    }

    if (Array.isArray(response)) {
      return response;
    }

    return [];
  }

  private extractSorts(response: any, activeSortCode: string): any[] {
    const incomingSorts = response?.sorts;

    if (Array.isArray(incomingSorts) && incomingSorts.length) {
      const hasCreationDateSort = incomingSorts.some((sort) => sort?.code === 'byCreationDate');
      const hasQuoteNumberSort = incomingSorts.some((sort) => sort?.code === 'byRFQNumber');

      if (hasCreationDateSort && hasQuoteNumberSort) {
        return incomingSorts.map((sort) => ({
          ...sort,
          selected: sort?.code === activeSortCode,
        }));
      }
    }

    return this.buildDefaultSorts(activeSortCode);
  }

  private extractPagination(response: any, currentCount: number, payload: any, activeSortCode: string): any {
    if (response?.pagination) {
      return {
        ...response.pagination,
        sort: activeSortCode || response.pagination.sort,
      };
    }

    const pageSize = Number(payload?.pageSize || 0);
    const currentPage = Number(payload?.currentPage || 0);

    if (pageSize > 0) {
      const estimatedTotalPages = Math.max(1, Math.ceil(currentCount / pageSize));
      return {
        currentPage,
        pageSize,
        totalResults: currentCount,
        totalPages: estimatedTotalPages,
        sort: activeSortCode,
      };
    }

    return {
      currentPage,
      totalResults: currentCount,
      totalPages: currentCount > 0 ? 1 : 0,
      sort: activeSortCode,
    };
  }

  private buildDefaultSorts(activeSortCode: string): any[] {
    return [
      {
        code: 'byCreationDate',
        selected: activeSortCode === 'byCreationDate',
      },
      {
        code: 'byRFQNumber',
        selected: activeSortCode === 'byRFQNumber',
      },
    ];
  }
}
